// React Hooks
import { useRef, useState, useEffect } from "react";

// LIBRERIE/IMPORTS
import { Link } from 'react-router-dom';
import { useTranslation } from "react-i18next";

// LIBRERIE ESTERNE
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBath, faBorderAll, faRulerCombined } from "@fortawesome/free-solid-svg-icons";
import Slider from "react-slick";

// CONFIGURAZIONI
import { AGENCY_ID } from "../../config/appConfig";

// HOOKS
import { useGetProperties } from "../../hooks/useGetProperties";

// COMPONENTI
import LoadingIndicator from "../Loaders/LoadingIndicator";

// Interfaccie Tipi
interface Vetrina2Props {
    agencyName: string;
}

const Vetrina2: React.FC<Vetrina2Props> = ({ agencyName }) => {
    const { t } = useTranslation();

    const { properties, loading, error } = useGetProperties(AGENCY_ID, {
            limit: 6,
            offset: 0,
            withCount: false,
            filters: {
                //vetrina: 2
            },
            orderBy: {
              field: '',
              direction: 'desc',
            },
            portal: 56  
        }
    );

    // Calcola dinamicamente slidesToShow in base alla larghezza dello schermo
    const [slidesToShow, setSlidesToShow] = useState(() => {
        if (typeof window !== 'undefined') {
            if (window.innerWidth < 768) return 1;
            if (window.innerWidth < 992) return 2;
        }
        return 3;
    });

    // Aggiorna slidesToShow al resize
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 768) {
                setSlidesToShow(1);
            } else if (window.innerWidth < 992) {
                setSlidesToShow(2);
            } else {
                setSlidesToShow(3);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const settings = {
        dots: true,
        arrows: false,
        infinite: true,
        speed: 1000,
        autoplay: true,
        autoplaySpeed: 4000,
        slidesToShow: slidesToShow,
        slidesToScroll: 1,
        swipeToSlide: true,
        onInit: () => setSwiping(false),
        beforeChange: () => setSwiping(true),
        afterChange: () => setSwiping(false),
    };

    // Stile + sostemato problema scorrimonto + click
    const [swiping, setSwiping] = useState(false);
    const sliderRef = useRef<Slider | null>(null);

    const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (swiping) e.preventDefault(); // blocca il click se stiamo swipando
    };

    // ---- Gestione Stati Iniziali ----
    if (loading) return <LoadingIndicator />;

    if (error) {
        console.error("Errore caricamento Vetrina:", error);
        return <div className="alert alert-warning">{t('common.loadingError', 'Errore caricamento dati.')}</div>;
    }

    if (!properties || properties.length === 0) {
        return (
            <div className="text-center p-5">{t('propertyList.notFound.title')}</div>
        )
    }


    // ---- Preparazione Variabili Comuni ----
    const fallbackImagePath = "/assets/images/no_image_big.jpg";
    const fallbackAltGlobal = t('property.imageAltFallback', 'Immagine non disponibile');

    return (
        <section id="vetrina2-box" className="space-ptb">
            <div className="container-fluid w-90">
                <p className="mb-0 sub-title">{t('homePage.vetrina.subTitle')}</p>
                <h2>{t('homePage.vetrina.title')}</h2>
                <div className="row">
                    <div className="col-12 px-xl-0">
                        <Slider ref={sliderRef} {...settings}>
                        {/* Utilizza VetrinaProperty per il tipo e slice/map */}
                        {(properties)?.slice(0, 6).map((immobile) => {

                            // --- Preparazione Variabili per il Loop ---
                            const propertyUrl = `/immobile/${immobile.id}`;
                            const locationName = immobile.addressDetails.quartiereName ?? immobile.addressDetails.frazioneName ?? immobile.addressDetails.comuneName ?? t('common.locationUnknown', 'Località sconosciuta');
                            const propertyTitle = `${immobile.tipologyDetails.name} in ${immobile.contractDetails.name} ${locationName == 'Centro' ? 'in' : 'a'} ${locationName}`;
                            const linkTitle = t('property.detailsTitlePattern', { propertyName: propertyTitle });
                            const imageUrl = immobile.mainImage || fallbackImagePath;
                            const imageAlt = (immobile.descriptions && immobile.descriptions[0]?.text.trim() !== "")
                                ? immobile.descriptions[0]?.text.substring(0, 100)
                                : `${propertyTitle} - ${agencyName}`;
                            const contractKey = `property.contractTypes.${immobile.contractDetails.name}`;
                            const contractName = t(contractKey, { defaultValue: immobile.contractDetails.name });
                            //const roomCount = immobile.rooms ?? 0;
                            const vani = immobile.premises ?? 0;
                            const bathroomCount = immobile.bathrooms ?? 0;

                            // --- Inizio JSX Card ---
                            return (
                                <div className="px-2 px-xl-3" key={immobile.id} >
                                    <Link to={propertyUrl} title={linkTitle ?? ''} className="property-item property-card d-block" onClick={handleLinkClick}>

                                        {/* Immagine */}
                                        <div className="property-image">
                                            <img
                                                className="img-fluid"
                                                src={imageUrl}
                                                alt={imageAlt}
                                                loading="lazy"
                                                onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                                                    const target = e.currentTarget;
                                                    const resolvedFallbackSrc = new URL(fallbackImagePath, window.location.origin).href;
                                                    if (target.src !== resolvedFallbackSrc) {
                                                        target.src = fallbackImagePath;
                                                    }
                                                    target.alt = fallbackAltGlobal;
                                                }}
                                            />
                                            {/* Badge Contratto */}
                                            <div className="property-lable">
                                                <span className="badge badge-md badge-primary">
                                                    {contractName}
                                                </span>
                                            </div>
                                            {/* Badge Esclusiva */}
                                            {immobile.exclusive && (
                                                <div className="property-esclusiva-box">
                                                    <span className="property-esclusiva">
                                                        {t('property.exclusive')}
                                                    </span>
                                                </div>
                                            )}
                                            {/* Prezzo */}
                                            <div className="property-price">
                                                {immobile.priceDetails.reservedNegotiation
                                                    ? t('property.priceReserved')
                                                    : `€ ${new Intl.NumberFormat('it-IT').format(immobile.priceDetails.price)} ${immobile.priceDetails.negotiable ? t('common.negotiable') : ""}`.trim()}
                                            </div>
                                        </div>

                                        {/* Dettagli Testuali */}
                                        <div className="property-details">
                                            <div className="property-details-inner">
                                                {/* Titolo */}
                                                <h5 className="property-title">
                                                    {immobile.tipologyDetails.name}
                                                </h5>
                                                {/* Indirizzo */}
                                                <p className="property-address">
                                                    {immobile.addressDetails.frazioneName || immobile.addressDetails.quartiereName ?
                                                        `${immobile.addressDetails.frazioneName || immobile.addressDetails.quartiereName}, ${immobile.addressDetails.comuneName} (${immobile.addressDetails.provinciaSigla ?? ""})`
                                                        : immobile.addressDetails.comuneName ?
                                                            `${immobile.addressDetails.comuneName} (${immobile.addressDetails.provinciaSigla ?? ""})`
                                                            : immobile.addressDetails.provinciaName ?? ''
                                                    }
                                                </p>
                                                {/* Info Vani/Bagni/Mq */}
                                                <ul className="property-info list-unstyled">
                                                    <li title={`${vani} ${t('common.room', { count: vani })}`}>
                                                        <i>
                                                            <FontAwesomeIcon icon={faBorderAll} />
                                                        </i>
                                                        <span>{vani}</span>
                                                        <span className="m-0">{t('common.premises')}</span>
                                                    </li>
                                                    {/* Bagni (mostra solo se > 0) */}
                                                    {bathroomCount > 0 && (
                                                        <li className="property-bath" title={`${bathroomCount} ${t('common.bathroom', { count: bathroomCount })}`}>
                                                            <i>
                                                                <FontAwesomeIcon icon={faBath} />
                                                            </i>
                                                            <span>{immobile.bathrooms}</span>
                                                            <span>
                                                                {immobile.bathrooms === 1 ? (t('common.bathroom') || "") : (t('common.bathrooms') || "")}
                                                            </span>
                                                        </li>
                                                    )}
                                                    {/* Mq (mostra solo se > 0) */}
                                                    {(immobile.sqmt ?? 0) > 0 && (
                                                        <li className="property-m-sqft" title={`${immobile.sqmt} m²`}>
                                                            <i>
                                                                <FontAwesomeIcon icon={faRulerCombined} />
                                                            </i>
                                                            <span className="small">{immobile.sqmt} </span>
                                                            <span>m<sup>2</sup></span>
                                                        </li>
                                                    )}
                                                </ul>
                                                {/* Riferimento */}
                                                <ul className="property-listing-actions list-unstyled mb-0 text-right">
                                                    <li className="property-rif">
                                                        {t('property.reference', 'Rif.')}. {immobile.ref}
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </Link>
                                </div>
                            );
                        })}
                        </Slider>
                    </div>
                </div>
            </div>
        </section >
    );
};

export default Vetrina2;